// The accounts filter bar, one component for All accounts and the board (My Accounts / Strategist
// Accounts), the same shape on both (GRO-600). Two rows, always on screen:
//   1. The quick chips, one wrapping row: High priority, then the owner chips (All accounts only:
//      Available, SDR-owned, AE-owned, Mine), then Fit and Engagement High / Mid / Low while scores
//      are drawn (`scores_visible`). Each with its live count.
//   2. The page's own controls (`children`): search, the ☰ Filters dropdown (`FiltersDropdown.tsx`)
//      and its active chips, the count, then Sort by or Export CSV.
// Both pages wrap it in `.filterbartop`, sticky from 768px up (all-accounts.css).
// The rules live in `allAccountsFilters.ts`, `filters.ts` and `scoreLevelFilter.ts`; this only draws them.

import type { ReactNode } from 'react';
import type { ScoreLevel } from './scores';
import type { BookColumn } from './columns';
import type { SortState } from './sort';
import { OWNER_CHIPS, OWNER_CHIP_HINT, OWNER_CHIP_LABEL, toggleValue } from './allAccountsFilters';
import type { OwnerChip } from './allAccountsFilters';
import { LEVELS, LEVEL_LABEL } from './scoreLevelFilter';
import type { ScoreLevelCounts, ScoreLevelFilters } from './scoreLevelFilter';
import { selectedSortKey, sortForOption, sortOptionsFor, unlistedSortLabel } from './sortOptions';
import { formatCount } from '../lib/format';

function Chip({
  label, count, pressed, hint, onToggle,
}: {
  label: string;
  count: number;
  pressed: boolean;
  hint: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={'aachip' + (pressed ? ' on' : '')}
      aria-pressed={pressed}
      // The label and count name the button; the hint is extra, so it must not replace them.
      aria-label={`${label}, ${count}`}
      title={hint}
      onClick={onToggle}
    >
      {label} <span className="aachipcount">{formatCount(count)}</span>
    </button>
  );
}

function LevelGroup({
  name, values, counts, onChange,
}: {
  name: string;
  values: ScoreLevel[];
  counts: Record<ScoreLevel, number>;
  onChange: (next: ScoreLevel[]) => void;
}) {
  return (
    <div className="aagroup" role="group" aria-label={name}>
      <span className="aagrouplabel">{name}</span>
      {LEVELS.map(level => (
        <Chip
          key={level}
          label={LEVEL_LABEL[level]}
          count={counts[level]}
          pressed={values.includes(level)}
          hint={`${name} ${LEVEL_LABEL[level].toLowerCase()}. Pick more than one to see either.`}
          onToggle={() => onChange(toggleValue(values, level))}
        />
      ))}
    </div>
  );
}

// The High priority chip: only the flagged rows when pressed. The AE page draws it on its own.
export interface HighPriorityChipState {
  pressed: boolean;
  count: number;
  onToggle: () => void;
}

export function HighPriorityChip({ state }: { state: HighPriorityChipState }) {
  return (
    <div className="aagroup" role="group" aria-label="Priority">
      <Chip
        label="High priority"
        count={state.count}
        pressed={state.pressed}
        hint="Only the accounts marked high priority."
        onToggle={state.onToggle}
      />
    </div>
  );
}

// Row 1's state, when the page has owner chips.
export interface OwnerChipRow {
  selected: OwnerChip[];
  counts: Record<OwnerChip, number>;
  onChange: (next: OwnerChip[]) => void;
}

export function AccountFilterBar({
  ownerChips, highPriority, scoreLevels, scoreCounts, scoresShown, onScoreLevelsChange, children,
}: {
  ownerChips?: OwnerChipRow;
  highPriority?: HighPriorityChipState;
  scoreLevels: ScoreLevelFilters;
  scoreCounts: ScoreLevelCounts;
  scoresShown: boolean;
  onScoreLevelsChange: (next: ScoreLevelFilters) => void;
  children?: ReactNode;
}) {
  // Nothing to draw: no owner chips, no priority chip, no scores and no controls.
  if (!ownerChips && !highPriority && !scoresShown && !children) return null;

  const hasChips = Boolean(ownerChips || highPriority || scoresShown);

  return (
    <section className="aafilterbar" aria-label="Filter accounts">
      {hasChips ? (
        <div className="aarow aachiprow">
          {highPriority ? <HighPriorityChip state={highPriority} /> : null}
          {ownerChips ? (
            <div className="aagroup" role="group" aria-label="Owner state">
              {OWNER_CHIPS.map(chip => (
                <Chip
                  key={chip}
                  label={OWNER_CHIP_LABEL[chip]}
                  count={ownerChips.counts[chip]}
                  pressed={ownerChips.selected.includes(chip)}
                  hint={OWNER_CHIP_HINT[chip]}
                  onToggle={() => ownerChips.onChange(toggleValue(ownerChips.selected, chip))}
                />
              ))}
            </div>
          ) : null}
          {scoresShown ? (
            <>
              <LevelGroup name="Fit" values={scoreLevels.fit_levels} counts={scoreCounts.fit_levels}
                onChange={next => onScoreLevelsChange({ ...scoreLevels, fit_levels: next })} />
              <LevelGroup name="Engagement" values={scoreLevels.engagement_levels} counts={scoreCounts.engagement_levels}
                onChange={next => onScoreLevelsChange({ ...scoreLevels, engagement_levels: next })} />
            </>
          ) : null}
        </div>
      ) : null}

      {children ? <div className="aarow aacontrolrow">{children}</div> : null}
    </section>
  );
}

// "Sort by": the column headers' sort, as a dropdown. A header click shows here too.
export function SortBySelect({
  sort, scoresShown, columns, onChange,
}: {
  sort: SortState;
  scoresShown: boolean;
  columns: BookColumn[];
  onChange: (next: SortState) => void;
}) {
  const selected = selectedSortKey(sort, scoresShown);
  return (
    <label className="aasortby">
      <span className="aagrouplabel">Sort by</span>
      <select className="aaselect" aria-label="Sort by" value={selected}
        onChange={event => onChange(sortForOption(event.target.value))}>
        {/* Only while no option names the sort: the header that set it, or the default order. */}
        {selected === '' ? <option value="" disabled>{unlistedSortLabel(sort, columns)}</option> : null}
        {sortOptionsFor(scoresShown).map(option => (
          <option key={option.key} value={option.key}>{option.label}{option.key === 'rank' ? ' (default)' : ''}</option>
        ))}
      </select>
    </label>
  );
}
